import type { Transaction } from './transaction.js'

/**
 * Uma compra parcelada é **uma compra**, e não N transações independentes.
 *
 * ## O que existia, e o que faltava
 *
 * `source.installment` carrega `number`, `total` e um `groupId` sintetizado na
 * ingestão desde a Fase 3 (decisão 130c): `pluggy:<data da compra>:<conta do
 * cartão>:<n de parcelas>` — a conta entrou no lugar do cartão físico na
 * decisão 281, porque o cartão físico partia 58 compras em duas. Ele nunca
 * tinha sido usado por nada. Medido na casa: **466 das 2808 transações são
 * parcelas, de 124 compras, e as 466 têm `groupId`** — a identidade da compra
 * já estava gravada; ninguém a lia.
 *
 * O que faltava era o enriquecimento respeitá-la. Categoria, observação e
 * discussão eram por linha, então classificar uma compra de 12x custava doze
 * decisões e um comentário escrito na 3/12 não existia para quem abrisse a
 * 4/12 — sendo que as duas são a mesma ida à loja.
 *
 * ## A identidade é a do PROVEDOR, e não uma que a gente adivinhe
 *
 * Nada aqui tenta ler `"Youcom 1/4"` para descobrir o grupo. A descrição não é
 * confiável para isso — a mesma compra chega com grafias diferentes a cada
 * parcela (`Cinemark Brasil S.A Ec 1/12` e `CINEMARK BRASIL S.A EC 4/12`) —, e
 * não é preciso: medido, **zero** descrições com `n/m` chegam sem `installment`
 * preenchido. Quando o provedor não diz, não há grupo, e cada linha é ela mesma.
 *
 * ## `null` é uma resposta, não uma ausência
 *
 * Compra à vista não tem grupo, e não deve ganhar um sintético só para o código
 * ficar uniforme: um grupo de um elemento convidaria a tratar toda transação
 * como parcelamento, e a primeira consequência seria uma consulta por grupo
 * para cada linha da lista.
 */
export function grupoDeParcelas(t: Transaction): string | null {
  return t.source.installment?.groupId ?? null
}

/**
 * A quem uma discussão pertence: à **compra**, quando há uma, senão à linha.
 *
 * `Comment` guarda `entityId` e a consulta parte dele (`comment-repository`
 * filtra por `entityId` só). Trocar o id da transação pelo id do grupo é o
 * suficiente para as parcelas dividirem a mesma thread — sem coleção nova, sem
 * migração e sem tocar na regra do Firestore.
 *
 * **A chave é opaca de propósito.** Quem chama não deve olhar para ela para
 * decidir nada; ela existe para endereçar a conversa, e o `entityType` continua
 * `'transaction'` porque é de uma transação que se está falando.
 */
export function chaveDeDiscussao(t: Transaction): string {
  return grupoDeParcelas(t) ?? t.id
}

/**
 * As transações que uma edição de enriquecimento alcança, dentro do conjunto
 * que quem chama tem em mãos.
 *
 * **Não é a lista completa do grupo, e não promete ser.** A propagação de
 * verdade é do gatilho `onTransactionWrite`, que enxerga o banco inteiro
 * — inclusive as parcelas de meses que a tela não carregou e as que ainda podem
 * chegar da Pluggy (medido: 6 das 37 que faltam estão depois da última
 * parcela conhecida). Isto aqui é o que a tela usa para se corrigir na
 * hora, sem esperar o servidor: a lista do mês já contém o grupo inteiro,
 * porque todas as parcelas de uma compra dividem a data da compra como
 * competência (medido: 124 de 124 compras num mês só).
 */
export function irmasNaMemoria(todas: readonly Transaction[], t: Transaction): Transaction[] {
  const grupo = grupoDeParcelas(t)
  if (grupo === null) return [t]
  const irmas = todas.filter((outra) => grupoDeParcelas(outra) === grupo)
  return irmas.length === 0 ? [t] : irmas
}

/**
 * Conta **compras**, e não linhas.
 *
 * É o número de decisões que faltam, que é o que a badge de "não categorizadas"
 * quer dizer — e com parcelamento os dois divergem muito: 462 das linhas sem
 * categoria desta casa são 123 compras. Dizer "58 não categorizadas" quando são
 * 21 decisões transforma um trabalho de minutos num que parece de horas.
 */
export function contarCompras(ts: readonly Transaction[]): number {
  const grupos = new Set<string>()
  let avulsas = 0
  for (const t of ts) {
    const grupo = grupoDeParcelas(t)
    if (grupo === null) avulsas += 1
    else grupos.add(grupo)
  }
  return avulsas + grupos.size
}

/** `null` à vista; senão, "1 de 4". */
export function rotuloDaParcela(t: Transaction): string | null {
  const p = t.source.installment
  return p === null ? null : `${String(p.number)} de ${String(p.total)}`
}
